/**
 * PortalLayout.jsx — Reusable sidebar + tool panel layout
 * Used by JobSearchPortal, FreelancerPortal, DeveloperPortal
 *
 * Usage:
 *   <PortalLayout
 *     title="AI Job Search Copilot"
 *     icon="🎯"
 *     color="#00c8f0"
 *     tagline="Match your resume to any job..."
 *     tools={TOOLS}
 *     otherPortals={OTHER_PORTALS}
 *   />
 */
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { TierGate, UpgradePrompt } from '../ProtectedRoute';

const T = {
  bg:'#07080c', bg2:'#0c0f18', bg3:'#111520', surf:'#161c2a',
  border:'rgba(255,255,255,.07)', borderH:'rgba(255,255,255,.13)',
  white:'#dce6fa', muted:'rgba(255,255,255,0.75)', dim:'rgba(255,255,255,0.75)',
  cyan:'#00c8f0', blue:'#4d8fff', green:'#2ecc71',
  sans:'"Syne","DM Sans",system-ui,sans-serif',
};

export default function PortalLayout({
  title,
  icon,
  color = T.cyan,
  tagline,
  tools = [],
  otherPortals = [],
  upgradeDesc,
}) {
  const [activeTool, setActiveTool] = useState(tools[0]?.id || '');
  const active          = tools.find(t => t.id === activeTool);
  const ActiveComponent = active?.component;

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Syne:wght@700;800;900&family=DM+Sans:wght@400;500;600&display=swap');
        * { box-sizing:border-box; margin:0; padding:0; }
        input::placeholder, textarea::placeholder { color:rgba(255,255,255,0.75)!important; }
        input:focus, textarea:focus, select:focus { border-color:rgba(0,200,240,.4)!important; outline:none; }
        @keyframes spin { to{transform:rotate(360deg)} }
        @keyframes nai-spin { to{transform:rotate(360deg)} }
        .portal-sidebar-link:hover { color:rgba(255,255,255,0.75) !important; }
      `}</style>

      <div style={{ background:T.bg, minHeight:'100vh', fontFamily:T.sans }}>

        {/* Top nav */}
        <div style={{
          background:T.bg2, borderBottom:`1px solid ${T.border}`,
          padding:'12px 20px', display:'flex', alignItems:'center',
          justifyContent:'space-between', position:'sticky', top:0, zIndex:20,
        }}>
          <div style={{ display:'flex', alignItems:'center', gap:12 }}>
            <Link to="/" style={{ fontSize:16, fontWeight:900, color:T.white, textDecoration:'none', letterSpacing:'-.02em' }}>
              Tylent<span style={{ color:T.cyan }}>ed</span>
            </Link>
            <span style={{ color:T.dim, fontSize:14 }}>›</span>
            <div style={{ display:'flex', alignItems:'center', gap:8 }}>
              <span style={{ fontSize:14, fontWeight:700, color:T.white }}>
                {icon} {title}
              </span>
              <span style={{
                padding:'2px 8px', borderRadius:20,
                background:`${color}12`, border:`1px solid ${color}35`,
                fontSize:10, fontWeight:700, color,
              }}>AI</span>
            </div>
          </div>
          <div style={{ display:'flex', gap:10, alignItems:'center' }}>
            <Link to="/pricing" style={{ fontSize:12.5, color:T.muted, textDecoration:'none' }}>Pricing</Link>
            <Link to="/dashboard" style={{
              padding:'6px 14px', borderRadius:7,
              border:`1px solid ${T.border}`, background:T.surf,
              color:T.white, fontSize:12.5, textDecoration:'none',
            }}>Dashboard</Link>
          </div>
        </div>

        {/* Portal hero strip */}
        <div style={{
          padding:'24px 24px 18px', textAlign:'center',
          background:`linear-gradient(180deg, ${color}05 0%, transparent 100%)`,
          borderBottom:`1px solid ${T.border}`,
        }}>
          <h1 style={{ fontSize:'clamp(1.2rem,2.5vw,1.7rem)', fontWeight:900, color:T.white, letterSpacing:'-.02em', marginBottom:5 }}>
            {icon} {title}
          </h1>
          <p style={{ fontSize:13, color:T.muted, maxWidth:560, margin:'0 auto' }}>{tagline}</p>
        </div>

        <div style={{ display:'flex', maxWidth:1280, margin:'0 auto' }}>

          {/* Sidebar */}
          <aside style={{
            width:210, flexShrink:0, borderRight:`1px solid ${T.border}`,
            background:T.bg2, minHeight:'calc(100vh - 126px)',
            display:'flex', flexDirection:'column',
          }}>
            <div style={{ padding:'14px 12px 6px', fontSize:10.5, fontWeight:700, color:T.dim, textTransform:'uppercase', letterSpacing:'.1em' }}>
              Tools
            </div>

            {tools.map(tool => {
              const isActive = activeTool === tool.id;
              return (
                <button key={tool.id} onClick={() => setActiveTool(tool.id)} style={{
                  width:'100%', padding:'9px 14px',
                  display:'flex', alignItems:'center', gap:9,
                  border:'none',
                  background:   isActive ? `${color}0a` : 'transparent',
                  borderRight:  isActive ? `2px solid ${color}` : '2px solid transparent',
                  color:        isActive ? T.white : T.muted,
                  cursor:'pointer', textAlign:'left', transition:'all .12s',
                  fontFamily:T.sans,
                }}>
                  <span style={{ fontSize:15, flexShrink:0 }}>{tool.icon}</span>
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ fontSize:12.5, fontWeight: isActive ? 600 : 400, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>
                      {tool.label}
                    </div>
                    <div style={{ fontSize:10.5, color:T.dim, marginTop:1, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>
                      {tool.desc}
                    </div>
                  </div>
                  {tool.badge && (
                    <span style={{ fontSize:9, padding:'1px 5px', borderRadius:20, background:`${T.green}18`, border:`1px solid ${T.green}30`, color:T.green, whiteSpace:'nowrap', flexShrink:0 }}>
                      {tool.badge}
                    </span>
                  )}
                </button>
              );
            })}

            {/* Other portals */}
            {otherPortals.length > 0 && (
              <div style={{ marginTop:'auto', padding:'14px 12px', borderTop:`1px solid ${T.border}` }}>
                <div style={{ fontSize:10.5, fontWeight:700, color:T.dim, textTransform:'uppercase', letterSpacing:'.1em', marginBottom:8 }}>
                  Switch portal
                </div>
                {otherPortals.map(p => (
                  <Link key={p.path} to={p.path} className="portal-sidebar-link" style={{
                    display:'block', padding:'5px 2px', fontSize:12.5,
                    color:T.dim, textDecoration:'none', transition:'color .12s',
                  }}>{p.label}</Link>
                ))}
              </div>
            )}
          </aside>

          {/* Main tool area */}
          <main style={{ flex:1, padding:'24px', overflow:'auto', minHeight:'calc(100vh - 126px)' }}>
            <TierGate
              requires="ai_tools"
              fallback={
                <UpgradePrompt
                  feature={title}
                  requiredTier="ai_tools"
                  description={upgradeDesc || `Get access to all ${tools.length} tools with a 7-day free trial.`}
                />
              }
            >
              {ActiveComponent ? (
                <div key={activeTool}>
                  <ActiveComponent/>
                </div>
              ) : (
                <div style={{ color:T.muted, textAlign:'center', paddingTop:48, fontSize:13 }}>
                  Select a tool from the sidebar.
                </div>
              )}
            </TierGate>
          </main>
        </div>
      </div>
    </>
  );
}
